import { useEffect } from 'react';
import './SeedBombAnimation.css';

/**
 * PROFESSIONAL SEED BOMB ANIMATION
 * Grass-type physical move with explosive seed projectile
 * 
 * Key Features:
 * - Grass-type physical attack (80 base power, 100% accuracy)
 * - Explosive seed projectile with burst mechanics
 * - Seed launching with realistic trajectory and explosion
 * - Professional gaming company quality with botanical timing
 * - Physical move animation with projectile and explosion mechanics
 */
export default function SeedBombAnimation({ playerRef, opponentRef, onComplete }) {
  useEffect(() => {
    const playerEl = playerRef.current;
    const opponentEl = opponentRef.current;
    if (!playerEl || !opponentEl) return;

    // Get battle container for positioning
    const container = playerEl.closest('.battle-screen') || playerEl.parentElement || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Calculate positions
    const playerRect = playerEl.getBoundingClientRect();
    const opponentRect = opponentEl.getBoundingClientRect();
    
    const playerX = Math.round((playerRect.left + playerRect.width / 2) - containerRect.left);
    const playerY = Math.round((playerRect.top + playerRect.height / 2) - containerRect.top);
    const opponentX = Math.round((opponentRect.left + opponentRect.width / 2) - containerRect.left);
    const opponentY = Math.round((opponentRect.top + opponentRect.height / 2) - containerRect.top);

    // Calculate projectile trajectory
    const dx = opponentX - playerX;
    const dy = opponentY - playerY;
    const distance = Math.hypot(dx, dy);

    // Elements to clean up
    const elements = [];

    // PHASE 1: Seed Preparation (0-500ms)
    console.log('Seed Bomb Phase 1: Seed Preparation');
    
    // Player enhancement with botanical aura
    playerEl.classList.add('seed-bomb-user-charge');
    
    // Botanical energy aura around player
    const botanicalAura = document.createElement('div');
    botanicalAura.className = 'seed-bomb-botanical-aura';
    botanicalAura.style.left = `${playerX - 90}px`;
    botanicalAura.style.top = `${playerY - 90}px`;
    container.appendChild(botanicalAura);
    elements.push(botanicalAura);

    // Seed energy particles around player
    for (let i = 0; i < 12; i++) {
      const seedParticle = document.createElement('div');
      seedParticle.className = 'seed-bomb-seed-particle';
      const angle = (i / 12) * 360;
      const radius = 60 + (i % 3) * 10;
      const particleX = playerX + Math.cos(angle * Math.PI / 180) * radius;
      const particleY = playerY + Math.sin(angle * Math.PI / 180) * radius;
      seedParticle.style.left = `${particleX - 4}px`;
      seedParticle.style.top = `${particleY - 4}px`;
      seedParticle.style.animationDelay = `${i * 35}ms`;
      seedParticle.textContent = '🌱';
      container.appendChild(seedParticle);
      elements.push(seedParticle);
    }

    // Leaf swirl effects showing grass energy
    for (let i = 0; i < 8; i++) {
      const leafSwirl = document.createElement('div');
      leafSwirl.className = 'seed-bomb-leaf-swirl';
      const swirlAngle = (i / 8) * 360;
      const swirlRadius = 75;
      const swirlX = playerX + Math.cos(swirlAngle * Math.PI / 180) * swirlRadius;
      const swirlY = playerY + Math.sin(swirlAngle * Math.PI / 180) * swirlRadius;
      leafSwirl.style.left = `${swirlX - 6}px`;
      leafSwirl.style.top = `${swirlY - 6}px`;
      leafSwirl.style.animationDelay = `${i * 50}ms`;
      leafSwirl.textContent = '🍃';
      container.appendChild(leafSwirl);
      elements.push(leafSwirl);
    }

    // PHASE 2: Seed Launch (500-900ms)
    const phase2Timer = setTimeout(() => {
      console.log('Seed Bomb Phase 2: Seed Launch');
      
      // Create the main seed projectile
      const seedProjectile = document.createElement('div');
      seedProjectile.className = 'seed-bomb-projectile';
      seedProjectile.style.left = `${playerX - 12}px`;
      seedProjectile.style.top = `${playerY - 12}px`;
      seedProjectile.style.setProperty('--target-x', `${dx}px`);
      seedProjectile.style.setProperty('--target-y', `${dy}px`);
      seedProjectile.textContent = '🌰';
      container.appendChild(seedProjectile);
      elements.push(seedProjectile);

      // Launch trail effects
      for (let i = 0; i < 6; i++) {
        const trailTimer = setTimeout(() => {
          const trail = document.createElement('div');
          trail.className = 'seed-bomb-launch-trail';
          const progress = i / 6;
          const trailX = playerX + (dx * progress * 0.3) + (Math.random() - 0.5) * 15;
          const trailY = playerY + (dy * progress * 0.3) + (Math.random() - 0.5) * 15;
          trail.style.left = `${trailX - 3}px`;
          trail.style.top = `${trailY - 3}px`;
          trail.textContent = '✨';
          container.appendChild(trail);
          elements.push(trail);
        }, i * 40);
        elements.push({ timer: trailTimer, type: 'timer' });
      }

      // Seed energy particles during flight
      for (let i = 0; i < 10; i++) {
        const energyTimer = setTimeout(() => {
          const energy = document.createElement('div');
          energy.className = 'seed-bomb-flight-energy';
          const progress = i / 10;
          const energyX = playerX + (dx * progress * 0.7) + (Math.random() - 0.5) * 20;
          const energyY = playerY + (dy * progress * 0.7) + (Math.random() - 0.5) * 20;
          energy.style.left = `${energyX - 4}px`;
          energy.style.top = `${energyY - 4}px`;
          energy.textContent = '🌿';
          container.appendChild(energy);
          elements.push(energy);
        }, i * 30);
        elements.push({ timer: energyTimer, type: 'timer' });
      }
    }, 500);
    elements.push({ timer: phase2Timer, type: 'timer' });

    // PHASE 3: Seed Explosion (900-1400ms)
    const phase3Timer = setTimeout(() => {
      console.log('Seed Bomb Phase 3: Seed Explosion');
      
      // Opponent hit reaction
      opponentEl.classList.add('seed-bomb-opponent-hit');

      // Massive seed explosion
      const seedExplosion = document.createElement('div');
      seedExplosion.className = 'seed-bomb-explosion';
      seedExplosion.style.left = `${opponentX - 120}px`;
      seedExplosion.style.top = `${opponentY - 120}px`;
      container.appendChild(seedExplosion);
      elements.push(seedExplosion);

      // Seed fragment burst
      for (let i = 0; i < 20; i++) {
        const fragment = document.createElement('div');
        fragment.className = 'seed-bomb-fragment';
        const fragmentAngle = (i / 20) * 360;
        const fragmentRadius = 70 + Math.random() * 50;
        const fragmentX = opponentX + Math.cos(fragmentAngle * Math.PI / 180) * fragmentRadius;
        const fragmentY = opponentY + Math.sin(fragmentAngle * Math.PI / 180) * fragmentRadius;
        fragment.style.left = `${fragmentX - 4}px`;
        fragment.style.top = `${fragmentY - 4}px`;
        fragment.style.animationDelay = `${i * 12}ms`;
        fragment.style.setProperty('--scatter-x', `${Math.cos(fragmentAngle * Math.PI / 180) * 40}px`);
        fragment.style.setProperty('--scatter-y', `${Math.sin(fragmentAngle * Math.PI / 180) * 40}px`);
        fragment.textContent = Math.random() > 0.5 ? '🌰' : '🌿';
        container.appendChild(fragment);
        elements.push(fragment);
      }

      // Explosion shockwaves
      for (let i = 0; i < 3; i++) {
        const shockwaveTimer = setTimeout(() => {
          const shockwave = document.createElement('div');
          shockwave.className = 'seed-bomb-shockwave';
          shockwave.style.left = `${opponentX - 80}px`;
          shockwave.style.top = `${opponentY - 80}px`;
          shockwave.style.animationDelay = `${i * 100}ms`;
          container.appendChild(shockwave);
          elements.push(shockwave);
        }, i * 120);
        elements.push({ timer: shockwaveTimer, type: 'timer' });
      }

      // Botanical energy burst
      for (let i = 0; i < 15; i++) {
        const energyBurst = document.createElement('div');
        energyBurst.className = 'seed-bomb-energy-burst';
        const burstAngle = (i / 15) * 360;
        const burstRadius = 90;
        const burstX = opponentX + Math.cos(burstAngle * Math.PI / 180) * burstRadius;
        const burstY = opponentY + Math.sin(burstAngle * Math.PI / 180) * burstRadius;
        energyBurst.style.left = `${burstX - 5}px`;
        energyBurst.style.top = `${burstY - 5}px`;
        energyBurst.style.animationDelay = `${i * 25}ms`;
        energyBurst.textContent = '🌱';
        container.appendChild(energyBurst);
        elements.push(energyBurst);
      }
    }, 900);
    elements.push({ timer: phase3Timer, type: 'timer' });

    // PHASE 4: Botanical Aftermath (1400-1700ms)
    const phase4Timer = setTimeout(() => {
      console.log('Seed Bomb Phase 4: Botanical Aftermath');
      
      // Remove charge effects
      playerEl.classList.remove('seed-bomb-user-charge');
      
      // Seed growth effects
      const seedGrowth = document.createElement('div');
      seedGrowth.className = 'seed-bomb-growth-effect';
      seedGrowth.style.left = `${opponentX - 70}px`;
      seedGrowth.style.top = `${opponentY - 70}px`;
      container.appendChild(seedGrowth);
      elements.push(seedGrowth);

      // Sprouting seeds around impact area
      for (let i = 0; i < 8; i++) {
        const sprout = document.createElement('div');
        sprout.className = 'seed-bomb-sprout';
        const sproutX = opponentX + (Math.random() - 0.5) * 120;
        const sproutY = opponentY + (Math.random() - 0.5) * 80;
        sprout.style.left = `${sproutX - 4}px`;
        sprout.style.top = `${sproutY - 4}px`;
        sprout.style.animationDelay = `${i * 40}ms`;
        sprout.textContent = '🌱';
        container.appendChild(sprout);
        elements.push(sprout);
      }

      // Floating leaves effect
      for (let i = 0; i < 6; i++) {
        const floatingLeaf = document.createElement('div');
        floatingLeaf.className = 'seed-bomb-floating-leaf';
        const leafX = opponentX + (Math.random() - 0.5) * 100;
        const leafY = opponentY - 40 + (Math.random() - 0.5) * 30;
        floatingLeaf.style.left = `${leafX - 4}px`;
        floatingLeaf.style.top = `${leafY - 4}px`;
        floatingLeaf.style.animationDelay = `${i * 60}ms`;
        floatingLeaf.textContent = '🍃';
        container.appendChild(floatingLeaf);
        elements.push(floatingLeaf);
      }
    }, 1400);
    elements.push({ timer: phase4Timer, type: 'timer' });

    // CLEANUP (1700ms)
    const cleanupTimer = setTimeout(() => {
      console.log('Seed Bomb: Animation Complete - Cleanup');
      
      // Remove all CSS classes
      playerEl.classList.remove('seed-bomb-user-charge');
      opponentEl.classList.remove('seed-bomb-opponent-hit');
      
      // Remove all created elements
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
      
      // Animation complete
      onComplete();
    }, 1700);

    // Cleanup function for component unmount
    return () => {
      clearTimeout(cleanupTimer);
      playerEl.classList.remove('seed-bomb-user-charge');
      opponentEl.classList.remove('seed-bomb-opponent-hit');
      elements.forEach(item => {
        if (item.type === 'timer') {
          clearTimeout(item.timer);
        } else if (item && item.parentNode) {
          item.remove();
        }
      });
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

// Set move type for battle system
SeedBombAnimation.moveType = 'grass';
